import {
    IonButton,
    IonButtons,
    IonCard,
    IonCardSubtitle,
    IonCardTitle,
    IonCol,
    IonContent,
    IonIcon,
    IonModal,
    IonRow,
    IonTitle,
    IonToolbar,
} from "@ionic/react";
import React, { useEffect, useState } from "react";
import { Organization, OrganizationDivision, PointTable } from "../../models";
import { getOrganizationById } from "../../utilities/organization/Organization";
import { getOrganizationDivisionsByOrganizationId, updateOrganizationDivision } from "../../utilities/organizationDivision/OrganizationDivision";
import { sortOrganizationDivisionsByNumber } from "../../utilities/organizationDivision/SortOrganizationDivisions";
import { Input, Table } from "reactstrap";
import OrganizationDivisionForm from "./OrganizationDivisionForm";
import { chevronDown, chevronUp, close, createOutline } from "ionicons/icons";
import Spinner from "../Spinners/Spinner";
import ErrorAlert from "../Errors/ErrorAlert";
import PriceField from "../Forms/PriceField";
import SelectCreatePointTable from "../PointTable/SelectCreatePointTable";
import { UpdateOrganizationDivisionInput } from "../../API";
import { formatJumpHeightString } from "../../utilities/jumpHeight/DisplayJumpHeights";

interface _Props {
    organizationId: string
}

enum sortOptions {
    number1 = "SortByNumberSmallToLarge",
    number2 = "SortByNumberLargeToSmall",
    name1 = "SortByNameAtoZ",
    name2 = "SortByNameZtoA"
}

const OrganizationDivisionEditTable: React.FC<_Props> = ({organizationId}) => {

    const [organizationDivisions, setOrganizationDivisions] = useState<OrganizationDivision[] | null | undefined>();
    const [selectedOrganizationDivision, setSelectedOrganizationDivision] = useState<OrganizationDivision | null | undefined>();
    const [showModal, setShowModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const [currentSortOption, setCurrentSortOption] = useState(sortOptions.number1);
    const [isEditNumber, setIsEditNumber] = useState(false);
    const [isEditName, setIsEditName] = useState(false);
    const [isEditEntryFee, setIsEditEntryFee] = useState(false);
    const [isEditPointTable, setIsEditPointTable] = useState(false);

    function sortOrganizationDivisions(currentSortOption: sortOptions) {
        if (organizationDivisions) {
            if (currentSortOption === sortOptions.number1) {
                const sorted = organizationDivisions.sort((a,b) => {
                    const numberResult = (a.number || 0) - (b.number || 0);
                    const nameResult = a.name.localeCompare(b.name);
                    return numberResult || nameResult;
                });
                setOrganizationDivisions(sorted);
            } else if (currentSortOption === sortOptions.number2) {
                const sorted = organizationDivisions.sort((a,b) => {
                    const numberResult = (b.number || 0) - (a.number || 0);
                    const nameResult = a.name.localeCompare(b.name);
                    return numberResult || nameResult;
                });
                setOrganizationDivisions(sorted);
            } else if (currentSortOption === sortOptions.name1) {
                const sorted = organizationDivisions.sort((a,b) => a.name.localeCompare(b.name));
                setOrganizationDivisions(sorted);
            } else if (currentSortOption === sortOptions.name2) {
                const sorted = organizationDivisions.sort((a,b) => b.name.localeCompare(a.name));
                setOrganizationDivisions(sorted);
            } 
        }
    }

    useEffect(() => {
        async function getOrganization() {
            const queryResult = await getOrganizationById(organizationId);
            if (queryResult.isSuccess) {
                getOrganizationDivisions(queryResult.result);
            } else {
                setError("Sorry, a problem occurred. Please go back and try again.");
            }
        }

        getOrganization();
    }, [organizationId]);

    async function getOrganizationDivisions(organization: Organization) {
        setIsLoading(true);
        const queryResult = await getOrganizationDivisionsByOrganizationId(organization.id);
        if (queryResult.isSuccess) {
            const list: OrganizationDivision[] = queryResult.result;
            const sorted = sortOrganizationDivisionsByNumber(list);
            if (sorted) {
                setOrganizationDivisions(sorted);
            } else {
                setOrganizationDivisions(list);
            }
        }
        setIsLoading(false);
    }

    const onSelectDivision = (orgDivision: OrganizationDivision) => {
        setSelectedOrganizationDivision(orgDivision);
        setShowModal(true);
    }

    const selectAddNewDivision = () => {
        setSelectedOrganizationDivision(undefined);
        setShowModal(true);
    }

    const handleOrganizationDivisionChange = (type: ("create" | "update" | "delete"), orgDivision: OrganizationDivision) => {
        setIsLoading(true);
        setShowModal(false);
        let updatedOrganizationDivisions: OrganizationDivision[] = [];

        if (type === "create") {
            if (organizationDivisions) {
                updatedOrganizationDivisions = organizationDivisions?.concat([orgDivision]);
                updatedOrganizationDivisions = sortOrganizationDivisionsByNumber(updatedOrganizationDivisions) || updatedOrganizationDivisions;
            } else {
                updatedOrganizationDivisions = [orgDivision];
            }
        } else if (type === "update") {
            if (organizationDivisions) {
                const index = organizationDivisions.findIndex(od => od.id === orgDivision.id);
                updatedOrganizationDivisions = [
                    ...organizationDivisions.slice(0, index),
                    orgDivision,
                    ...organizationDivisions.slice(index + 1)
                ];
            }
        } else if (type === "delete") {
            if (organizationDivisions) {
                updatedOrganizationDivisions = organizationDivisions.filter(od => od.id !== orgDivision.id);
            }
        }

        setOrganizationDivisions(updatedOrganizationDivisions);
        setSelectedOrganizationDivision(undefined);
        setIsLoading(false);
    }

    const handleNumberChange = (orgDivision: OrganizationDivision, index: number, number?: number) => {
        if (organizationDivisions) {
            let updatedOrganizationDivisions = [
                ...organizationDivisions.slice(0, index),
                {
                    ...orgDivision,
                    number: number
                },
                ...organizationDivisions.slice(index + 1)
            ];
            setOrganizationDivisions(updatedOrganizationDivisions);
        }
    }

    const handleNameChange = (orgDivision: OrganizationDivision, index: number, name: string) => {
        if (organizationDivisions) {
            let updatedOrganizationDivisions = [
                ...organizationDivisions.slice(0, index),
                {
                    ...orgDivision,
                    name: name
                },
                ...organizationDivisions.slice(index + 1)
            ];
            setOrganizationDivisions(updatedOrganizationDivisions);
        }
    }

    const handleEntryFeeChange = (orgDivision: OrganizationDivision, index: number, entryFee: number) => {
        if (organizationDivisions) {
            const updatedOrgDivision: OrganizationDivision = {
                ...orgDivision,
                entryFee: (entryFee === 0 ? null : entryFee.toString())
            };

            let updatedOrganizationDivisions = [
                ...organizationDivisions.slice(0, index),
                updatedOrgDivision,
                ...organizationDivisions.slice(index + 1)
            ];
            setOrganizationDivisions(updatedOrganizationDivisions);
        }
    }

    const handlePointTableChange = (orgDivision: OrganizationDivision, index: number, pointTable: PointTable) => {
        if (organizationDivisions) {
            const updatedOrgDivision: OrganizationDivision = {
                ...orgDivision,
                organizationDivisionPointTableId: pointTable.id || "",
                pointTable: pointTable
            };

            let updatedOrganizationDivisions = [
                ...organizationDivisions.slice(0, index),
                updatedOrgDivision,
                ...organizationDivisions.slice(index + 1)
            ];
            setOrganizationDivisions(updatedOrganizationDivisions);
        }
    }

    const handleSave = async () => {
        setIsLoading(true);
        if (organizationDivisions) {
            for (let i = 0; i < organizationDivisions.length; i++) {
                const orgDivision = organizationDivisions[i];
                const input: UpdateOrganizationDivisionInput = {
                    id: orgDivision.id,
                    name: orgDivision.name,
                    number: orgDivision.number,
                    entryFee: orgDivision.entryFee,
                    organizationDivisionPointTableId: orgDivision.pointTable?.id || ""
                };
                const updateResult = await updateOrganizationDivision(input);
            }
        }
        setIsEditName(false);
        setIsEditNumber(false);
        setIsEditEntryFee(false);
        setIsEditPointTable(false);
        setIsLoading(false);
    }

    return (
        <>
            <IonCard mode="md" className="ion-padding bg-white">
                <IonCardTitle>Organization Divisions</IonCardTitle>
                <IonCardSubtitle>
                    <IonRow>
                        <IonCol sizeXs="12" sizeMd="6">
                            <p>Click on a division to edit it.</p>
                        </IonCol>
                        <IonCol sizeXs="12" sizeMd="6" className="ion-text-right">
                            <IonButton onClick={selectAddNewDivision}>
                                Add Division
                            </IonButton>
                        </IonCol>
                    </IonRow>
                </IonCardSubtitle>
                {error && <ErrorAlert error={error} />}
                {(isEditEntryFee || isEditName || isEditNumber || isEditPointTable) && (
                    <IonRow className="ion-justify-content-center">
                        <IonCol sizeXs="12" sizeMd="6" className="ion-text-center">
                            <IonButton color="success" onClick={() => handleSave()}>
                                Save Edits
                            </IonButton>
                        </IonCol>
                    </IonRow>
                )}
                {isLoading ?
                    <Spinner />
                    :
                    <IonRow>
                        <IonCol>
                            {(organizationDivisions && organizationDivisions.length > 0) ? 
                                <Table hover responsive>
                                    <thead>
                                        <tr>
                                            <th>
                                                <p>
                                                    <span onClick={() => {setCurrentSortOption(currentSortOption === sortOptions.number1 ? sortOptions.number2 : sortOptions.number1); sortOrganizationDivisions(currentSortOption === sortOptions.number1 ? sortOptions.number2 : sortOptions.number1);} }>
                                                        Number
                                                    </span>
                                                    <IonIcon icon={currentSortOption === sortOptions.number1 ? chevronDown : chevronUp} onClick={() => {setCurrentSortOption(currentSortOption === sortOptions.number1 ? sortOptions.number2 : sortOptions.number1); sortOrganizationDivisions(currentSortOption === sortOptions.number1 ? sortOptions.number2 : sortOptions.number1);} } /> 
                                                    <IonIcon icon={createOutline} ariaLabel="Edit Numbers" onClick={() => setIsEditNumber(true)} />
                                                </p>
                                            </th>
                                            <th>
                                                <p>
                                                    <span onClick={() => {setCurrentSortOption(currentSortOption === sortOptions.name1 ? sortOptions.name2 : sortOptions.name1); sortOrganizationDivisions(currentSortOption === sortOptions.name1 ? sortOptions.name2 : sortOptions.name1);} }>
                                                        Name
                                                    </span>
                                                    <IonIcon icon={currentSortOption === sortOptions.name1 ? chevronDown : chevronUp} onClick={() => {setCurrentSortOption(currentSortOption === sortOptions.name1 ? sortOptions.name2 : sortOptions.name1); sortOrganizationDivisions(currentSortOption === sortOptions.name1 ? sortOptions.name2 : sortOptions.name1);} } /> 
                                                    <IonIcon icon={createOutline} ariaLabel="Edit Names" onClick={() => setIsEditName(true)} />
                                                </p>
                                            </th>
                                            <th><p>Jump Height(s)</p></th>
                                            <th>
                                                <p>
                                                    <span>
                                                        Entry Fee
                                                    </span>
                                                    <IonIcon icon={createOutline} ariaLabel="Edit Names" onClick={() => setIsEditEntryFee(true)} />
                                                </p>
                                            </th>
                                            <th>
                                                <p>
                                                    <span>
                                                        Point Table
                                                    </span>
                                                    <IonIcon icon={createOutline} ariaLabel="Edit Names" onClick={() => setIsEditPointTable(true)} />
                                                </p>
                                            </th>
                                            <th><p>Prize Money</p></th>
                                            <th><p>USEF</p></th>
                                            <th><p>USHJA</p></th>
                                            <th><p>EC</p></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {organizationDivisions.map((od, index) => (
                                            <tr key={index} onClick={() => (isEditEntryFee || isEditName || isEditNumber || isEditPointTable) ? console.log("Is editing") : onSelectDivision(od)}>
                                                <td>
                                                    {isEditNumber ? 
                                                        <>
                                                            <Input
                                                                className="input-width"
                                                                type="text"
                                                                value={od.number || 0}
                                                                onChange={e => {
                                                                    handleNumberChange(od, index, (e.target.value ? parseInt(e.target.value) : undefined));
                                                                }}
                                                            />
                                                        </>
                                                        :
                                                        <>
                                                            {od?.number || ""}
                                                        </>
                                                    }
                                                </td>
                                                <td>
                                                    {isEditName ? 
                                                        <>
                                                            <Input
                                                                className="extra-large-input-width"
                                                                type="text"
                                                                value={od.name || 0}
                                                                onChange={e => {
                                                                    handleNameChange(od, index, e.target.value);
                                                                }}
                                                            />
                                                        </>
                                                        :
                                                        <>
                                                            {od?.name || ""}
                                                        </>
                                                    }
                                                </td>
                                                <td>{od.jumpHeight ? formatJumpHeightString(od.jumpHeight) : ""}</td>
                                                <td>
                                                    {isEditEntryFee ? 
                                                        <>
                                                            <PriceField value={od.entryFee ? parseFloat(od.entryFee) : undefined} className="ion-margin-top" onChange={(value: number) => handleEntryFeeChange(od, index, value)}/>
                                                        </>
                                                        :
                                                        <>
                                                            {od.entryFee ? ("$" + od.entryFee) : ""}
                                                        </>
                                                    }
                                                </td>
                                                <td>
                                                    {isEditPointTable ? 
                                                        <div className="extra-large-input-width">
                                                            <SelectCreatePointTable organizationId={organizationId} pointTable={od.pointTable} onSelect={(pt: PointTable) => handlePointTableChange(od, index, pt)}/>
                                                        </div>
                                                        :
                                                        <>
                                                            {od.pointTable?.name || ""}
                                                        </>
                                                    }
                                                </td>
                                                <td>{od.prizeMoney ? ("$" + od.prizeMoney) : ""}</td>
                                                <td>{od.division.usefNumber || ""}</td>
                                                <td>{od.division.ushjaName || ""}</td>
                                                <td>{od.division.ecNumber || ""}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                :
                                <p>No organization divisions found.</p>
                            }
                        </IonCol>
                    </IonRow>
                }
            </IonCard>
            <IonModal backdropDismiss={false} isOpen={showModal} id="organizationDivisionModal">
                <IonToolbar color="light">
                    <IonTitle className="ion-text-center">
                        {selectedOrganizationDivision ? selectedOrganizationDivision.name : "Organization Division"} 
                    </IonTitle>
                    <IonButtons slot="end">
                        <IonButton
                            fill="clear"
                            onClick={() => setShowModal(false)}
                        >
                            <p id="organizationDivisionModalBtn" className="font-weight-normal text-medium text-capitalize">
                                <IonIcon icon={close} />
                            </p>
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
                <IonContent className="ion-padding">
                    <OrganizationDivisionForm organizationId={organizationId} organizationDivision={selectedOrganizationDivision} onSubmit={handleOrganizationDivisionChange} />
                </IonContent>
            </IonModal>
        </>
    );
};

export default OrganizationDivisionEditTable;